// FoalingRecords.js — الولادات والمواليد وسلسلة النسب (v3.5.1، منقول عن Track B).
//
// بطاقة «سلسلة النسب» أدناه ليست زينة: هي المخرَج الذي يترجم غرض الميزة كلّها — توثيق نسب
// يحلّ محلّ الورق. والقاعدة الحاكمة معروضة صراحةً في الواجهة: في نقل الأجنّة تُسجَّل الولادة
// على حالة **الحاضنة**، لكنّ أمّ المولود نَسَبيًّا هي **المانحة**. تُعرَض مع سبب الاشتقاق
// لأنّ توثيقًا لا يفسّر نفسه يُصدَّق بلا فحص — وذلك أخطر من غيابه.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import api from '../../services/api';
import { nowDateTimeInput } from '../../utils/dateUtils';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

// 3.6.7 — نموذج تسجيل واقعة الولادة. كان `POST /reproduction-clinic/foaling` موجودًا في
// الخادم منذ البداية ولا تستدعيه أيّ واجهة، بينما هذه الصفحة نفسها تقول للمستخدم «سجّل
// الولادة أوّلًا ثمّ أضف المواليد» — أي تطلب خطوةً لا سبيل إليها. هذا هو السبيل.
//
// الحقول الإلزاميّة واحد فقط: وقت خروج المولود. والباقي علامات ما قبل الولادة ونتيجتها،
// تُملأ بقدر ما رُصد — فالولادة تُسجَّل غالبًا بعد وقوعها لا أثناءها.
const FOALING_RESULTS = ['Normal', 'Assisted', 'Emergency', 'Stillbirth', 'Unknown'];

/** تسمية نتيجة الولادة بلغة الشاشة — المفتاح إنجليزيّ في القاعدة ولا يُعرض كما هو. */
function foalingResultLabel(result, text) {
  const map = {
    Normal: text.resultNormal,
    Assisted: text.resultAssisted,
    Emergency: text.resultEmergency,
    Stillbirth: text.resultStillbirth,
    Unknown: text.resultUnknown,
  };
  return map[result] || result || text.resultUnknown;
}

function createEmptyFoaling() {
  return {
    monitoring_start_date: '',
    udder_development: false,
    waxing: false,
    milk_leakage: false,
    pelvic_ligament_relaxation: false,
    water_broken_time: '',
    foal_delivered_time: nowDateTimeInput(),
    placenta_delivered_time: '',
    emergency_alert: false,
    emergency_reason: '',
    foaling_result: 'Normal',
    notes: '',
  };
}

const COPY = {
  ar: {
    title: 'الولادات والمواليد',
    subtitle: 'تسجيل المواليد وجنسها، وترقيتها إلى سجل الخيل بنسب مشتق من سلسلة الحالات لا مكتوب باليد.',
    refresh: 'تحديث',
    selectCase: 'اختر الحالة',
    noFoalingRecord: 'لا توجد واقعة ولادة مسجلة لهذه الحالة بعد. سجّل الولادة أولاً ثم أضف المواليد.',
    recordFoaling: 'تسجيل واقعة الولادة',
    foalingDetails: 'تفاصيل الولادة',
    monitoringStart: 'بداية المراقبة',
    udderDevelopment: 'تطور الضرع',
    waxing: 'تشمّع الحلمات',
    milkLeakage: 'تسرّب الحليب',
    pelvicRelaxation: 'ارتخاء أربطة الحوض',
    waterBroken: 'وقت نزول الماء',
    foalDelivered: 'وقت خروج المولود',
    placentaDelivered: 'وقت نزول المشيمة',
    emergencyAlert: 'حالة طارئة',
    emergencyReason: 'سبب الطارئ',
    foalingResult: 'نتيجة الولادة',
    foalingSaved: 'تم تسجيل واقعة الولادة.',
    foalDeliveredRequired: 'وقت خروج المولود مطلوب.',
    resultNormal: 'طبيعية',
    resultAssisted: 'بمساعدة',
    resultEmergency: 'طارئة',
    resultStillbirth: 'ميت عند الولادة',
    resultUnknown: 'غير محدد',
    foalings: 'وقائع ولادة',
    foals: 'مواليد الحالة',
    alive: 'حي',
    registeredCount: 'مسجل في الخيل',
    pedigreeChain: 'سلسلة النسب',
    viaTransfer: 'عبر نقل جنين',
    viaDirect: 'حمل مباشر',
    transferNote: 'الولادة مسجلة على الحاضنة، لكن أم المولود نَسَبياً هي المانحة — والنظام يشتقها من سجل النقل لا من حالة الولادة.',
    sire: 'الأب (الفحل)',
    dam: 'الأم (المانحة)',
    carriedBy: 'حملتها ووَلدتها',
    unknown: 'غير مسجل',
    case: 'حالة',
    foalsTitle: 'المواليد',
    foalsHint: 'الولادة قد تُنتج توأماً — أضف كل مولود على حدة بجنسه وحالته.',
    addFoal: 'إضافة مولود',
    noFoals: 'لا توجد مواليد مسجلة لهذه الحالة.',
    sex: 'الجنس',
    male: 'ذكر',
    female: 'أنثى',
    sexUnknown: 'غير محدد',
    status: 'الحالة',
    stillborn: 'ميت عند الولادة',
    diedAfter: 'نفق بعد الولادة',
    birthTime: 'وقت الولادة',
    markings: 'العلامات',
    notes: 'ملاحظات',
    registerFoal: 'تسجيل في سجل الخيل',
    registeredAs: 'مسجل في سجل الخيل',
    notRegisterable: 'لا يُسجل في سجل الخيل — والواقعة موثقة هنا.',
    registerTitle: 'تسجيل المولود في سجل الخيل',
    registerNote: 'النسب وتاريخ الميلاد يُشتقان تلقائياً من سلسلة الحالات ولا يُكتبان يدوياً. المالك مقترح ويمكن تعديله قبل التأكيد.',
    nameAr: 'الاسم بالعربية',
    nameEn: 'الاسم بالإنجليزية',
    owner: 'المالك (مقترح)',
    confirmRegister: 'تسجيل',
    cancel: 'إلغاء',
    save: 'حفظ',
    needName: 'اسم المولود مطلوب.',
    foalSaved: 'تم تسجيل المولود.',
    registered: 'سُجل المولود في سجل الخيل بنسبه.',
    loadError: 'تعذر تحميل بيانات الولادة',
    saveError: 'تعذر الحفظ.',
  },
  en: {
    title: 'Foalings and foals',
    subtitle: 'Record foals and their sex, and promote them into the horse registry with parentage derived from the case chain rather than typed by hand.',
    refresh: 'Refresh',
    selectCase: 'Select case',
    noFoalingRecord: 'No foaling recorded for this case yet. Record the foaling first, then add foals.',
    recordFoaling: 'Record foaling',
    foalingDetails: 'Foaling details',
    monitoringStart: 'Monitoring start',
    udderDevelopment: 'Udder development',
    waxing: 'Waxing',
    milkLeakage: 'Milk leakage',
    pelvicRelaxation: 'Pelvic ligament relaxation',
    waterBroken: 'Water broken at',
    foalDelivered: 'Foal delivered at',
    placentaDelivered: 'Placenta delivered at',
    emergencyAlert: 'Emergency',
    emergencyReason: 'Emergency reason',
    foalingResult: 'Foaling result',
    foalingSaved: 'Foaling recorded.',
    foalDeliveredRequired: 'Delivery time is required.',
    resultNormal: 'Normal',
    resultAssisted: 'Assisted',
    resultEmergency: 'Emergency',
    resultStillbirth: 'Stillbirth',
    resultUnknown: 'Unknown',
    foalings: 'Foaling records',
    foals: 'Foals in case',
    alive: 'Alive',
    registeredCount: 'Registered as horses',
    pedigreeChain: 'Pedigree chain',
    viaTransfer: 'Via embryo transfer',
    viaDirect: 'Direct pregnancy',
    transferNote: 'The foaling is recorded against the recipient, but the foal’s dam is the donor — derived from the transfer record, not from the foaling case.',
    sire: 'Sire',
    dam: 'Dam (donor)',
    carriedBy: 'Carried and delivered by',
    unknown: 'Not recorded',
    case: 'Case',
    foalsTitle: 'Foals',
    foalsHint: 'A foaling may produce twins — add each foal separately with its sex and status.',
    addFoal: 'Add foal',
    noFoals: 'No foals recorded for this case.',
    sex: 'Sex',
    male: 'Male',
    female: 'Female',
    sexUnknown: 'Not set',
    status: 'Status',
    stillborn: 'Stillborn',
    diedAfter: 'Died after birth',
    birthTime: 'Birth time',
    markings: 'Markings',
    notes: 'Notes',
    registerFoal: 'Register as horse',
    registeredAs: 'Registered in the horse list',
    notRegisterable: 'Not registered as a horse — the event is documented here regardless.',
    registerTitle: 'Register foal as horse',
    registerNote: 'Parentage and date of birth are derived automatically from the case chain, not typed. Owner is proposed and can be changed before confirming.',
    nameAr: 'Name (Arabic)',
    nameEn: 'Name (English)',
    owner: 'Owner (proposed)',
    confirmRegister: 'Register',
    cancel: 'Cancel',
    save: 'Save',
    needName: 'A name is required.',
    foalSaved: 'Foal recorded.',
    registered: 'Foal registered with its pedigree.',
    loadError: 'Could not load foaling data',
    saveError: 'Could not save.',
  },
};

const caseName = (row) => (row ? [row.inside_horse_name_ar, row.inside_horse_name].filter(Boolean).join(' | ') || `#${row.case_id}` : '-');

const nowLocal = () => new Date().toISOString().slice(0, 16);

export default function FoalingRecords() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];

  const [cases, setCases] = useState([]);
  const [foalings, setFoalings] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [foals, setFoals] = useState([]);
  const [pedigree, setPedigree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [foalingDialog, setFoalingDialog] = useState(false);
  const [foalingForm, setFoalingForm] = useState(createEmptyFoaling);
  const [foalDialog, setFoalDialog] = useState(false);
  const [foalForm, setFoalForm] = useState({ sex: '', status: 'alive', birth_time: nowLocal(), markings: '', notes: '' });
  const [registerTarget, setRegisterTarget] = useState(null);
  const [registerForm, setRegisterForm] = useState({ name: '', name_arabic: '', owner_id: '' });

  const selectedCase = useMemo(() => cases.find((c) => String(c.case_id) === selectedCaseId) || null, [cases, selectedCaseId]);
  const foaling = useMemo(() => foalings.find((f) => String(f.case_id) === selectedCaseId) || null, [foalings, selectedCaseId]);

  const stats = useMemo(() => ({
    foalings: foalings.length,
    foals: foals.length,
    alive: foals.filter((f) => f.status === 'alive').length,
    registered: foals.filter((f) => f.horse_id).length,
  }), [foalings, foals]);

  const loadBase = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [casesRes, foalingRes] = await Promise.all([
        api.get('/reproduction-clinic/cases'),
        api.get('/reproduction-clinic/foaling'),
      ]);
      setCases(Array.isArray(casesRes.data) ? casesRes.data : []);
      setFoalings(Array.isArray(foalingRes.data) ? foalingRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [text.loadError]);

  // ---- تسجيل واقعة الولادة (3.6.7) --------------------------------------
  const openFoalingDialog = () => {
    setFoalingForm(createEmptyFoaling());
    setFoalingDialog(true);
  };

  const submitFoaling = async () => {
    if (!selectedCaseId) return;
    // الوقت وحده إلزاميّ: هو ما يُشتقّ منه تاريخ ميلاد المولود لاحقًا عند تسجيله خيلًا.
    if (!foalingForm.foal_delivered_time) {
      setError(text.foalDeliveredRequired);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post('/reproduction-clinic/foaling', {
        case_id: Number(selectedCaseId),
        ...foalingForm,
        monitoring_start_date: foalingForm.monitoring_start_date || null,
        water_broken_time: foalingForm.water_broken_time || null,
        placenta_delivered_time: foalingForm.placenta_delivered_time || null,
        emergency_reason: foalingForm.emergency_reason || null,
        notes: foalingForm.notes || null,
      });
      setFoalingDialog(false);
      setMessage(text.foalingSaved);
      await loadBase();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || text.saveError);
    } finally {
      setBusy(false);
    }
  };

  const loadCaseDetail = useCallback(async (caseId) => {
    if (!caseId) { setFoals([]); setPedigree(null); return; }
    try {
      const [foalsRes, pedigreeRes] = await Promise.all([
        api.get('/reproduction-clinic/foals', { params: { case_id: caseId } }),
        api.get(`/reproduction-clinic/cases/${caseId}/foal-pedigree`),
      ]);
      setFoals(Array.isArray(foalsRes.data) ? foalsRes.data : []);
      setPedigree(pedigreeRes.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || text.loadError);
    }
  }, [text.loadError]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadCaseDetail(selectedCaseId); }, [loadCaseDetail, selectedCaseId]);

  const addFoal = async () => {
    if (!foaling) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await api.post('/reproduction-clinic/foals', {
        foaling_id: foaling.foaling_id,
        sex: foalForm.sex || null,
        status: foalForm.status,
        birth_time: foalForm.birth_time || null,
        markings: foalForm.markings || null,
        notes: foalForm.notes || null,
      });
      setFoalDialog(false);
      setFoalForm({ sex: '', status: 'alive', birth_time: nowLocal(), markings: '', notes: '' });
      setMessage(text.foalSaved);
      await loadCaseDetail(selectedCaseId);
    } catch (e) {
      setError(e?.response?.data?.message || text.saveError);
    } finally {
      setBusy(false);
    }
  };

  const registerFoal = async () => {
    if (!registerTarget) return;
    if (!registerForm.name && !registerForm.name_arabic) { setError(text.needName); return; }
    setBusy(true); setError('');
    try {
      await api.post(`/reproduction-clinic/foals/${registerTarget.foal_id}/register-horse`, {
        name: registerForm.name || null,
        name_arabic: registerForm.name_arabic || null,
        owner_id: registerForm.owner_id ? Number(registerForm.owner_id) : null,
      });
      setRegisterTarget(null);
      setRegisterForm({ name: '', name_arabic: '', owner_id: '' });
      setMessage(text.registered);
      await loadCaseDetail(selectedCaseId);
    } catch (e) {
      setError(e?.response?.data?.message || text.saveError);
    } finally {
      setBusy(false);
    }
  };

  const openRegister = (foal) => {
    setRegisterForm({ name: '', name_arabic: '', owner_id: pedigree?.dam_owner_id ? String(pedigree.dam_owner_id) : '' });
    setRegisterTarget(foal);
    setError('');
  };

  const sexLabel = (sex) => (sex === 'S' ? text.male : sex === 'M' ? text.female : text.sexUnknown);
  const statusLabel = (status) => (status === 'alive' ? text.alive : status === 'stillborn' ? text.stillborn : text.diedAfter);

  return (
    <Box className="smart-page-container" dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={text.title}
        subtitle={text.subtitle}
        actions={(
          <Button onClick={loadBase} startIcon={<RefreshIcon />} sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.refresh}</Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2.2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}><Metric icon={<ChildFriendlyIcon />} label={text.foalings} value={stats.foalings} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<ChildFriendlyIcon />} label={text.foals} value={stats.foals} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<ChildFriendlyIcon />} label={text.alive} value={stats.alive} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<CheckIcon />} label={text.registeredCount} value={stats.registered} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <TextField
          select
          fullWidth
          label={text.selectCase}
          value={selectedCaseId}
          onChange={(e) => setSelectedCaseId(e.target.value)}
          sx={{ maxWidth: 560 }}
        >
          {cases.map((c) => (
            <MenuItem key={c.case_id} value={String(c.case_id)}>
              {`${caseName(c)} — ${c.case_type || ''} ${c.case_status ? `(${c.case_status})` : ''}`.trim()}
            </MenuItem>
          ))}
        </TextField>
        {selectedCase && !foaling && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openFoalingDialog} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
                {text.recordFoaling}
              </Button>
            }
          >
            {text.noFoalingRecord}
          </Alert>
        )}
        {selectedCase && foaling && (
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mt: 2 }}>
            <Chip label={`${text.foalDelivered}: ${foaling.foal_delivered_time ? String(foaling.foal_delivered_time).replace('T', ' ').slice(0, 16) : '-'}`} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 900 }} />
            <Chip label={`${text.foalingResult}: ${foalingResultLabel(foaling.foaling_result, text)}`} sx={{ bgcolor: foaling.foaling_result === 'Stillbirth' || foaling.foaling_result === 'Emergency' ? COLORS.roseSoft : COLORS.greenSoft, fontWeight: 900 }} />
            {Number(foaling.emergency_alert) === 1 && <Chip label={text.emergencyAlert} sx={{ bgcolor: COLORS.roseSoft, fontWeight: 900 }} />}
          </Stack>
        )}
      </Paper>

      {selectedCaseId && pedigree && (
        <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1} sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.pedigreeChain}</Typography>
            <Chip
              label={pedigree.via === 'embryo_transfer' ? text.viaTransfer : text.viaDirect}
              sx={{ bgcolor: pedigree.via === 'embryo_transfer' ? COLORS.greenSoft : COLORS.cardSoft, fontWeight: 900 }}
            />
          </Stack>

          {pedigree.via === 'embryo_transfer' && <Alert severity="info" sx={{ mb: 1.5 }}>{text.transferNote}</Alert>}

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.sire}</Typography>
                <Typography sx={{ fontWeight: 900, color: COLORS.text }}>
                  {pedigree.sire_stallion_name || (pedigree.sire_stallion_id ? `#${pedigree.sire_stallion_id}` : text.unknown)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.dam}</Typography>
                <Typography sx={{ fontWeight: 900, color: COLORS.text }}>
                  {pedigree.dam_case_id ? caseName(cases.find((c) => c.case_id === pedigree.dam_case_id)) : text.unknown}
                </Typography>
                {pedigree.dam_case_id ? (
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>{`${text.case} #${pedigree.dam_case_id}`}</Typography>
                ) : null}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.carriedBy}</Typography>
                <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{caseName(selectedCase)}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {selectedCaseId && (
        <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.foalsTitle}</Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.foalsHint}</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!foaling}
              onClick={() => { setFoalForm({ sex: '', status: 'alive', birth_time: nowLocal(), markings: '', notes: '' }); setError(''); setFoalDialog(true); }}
              sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
            >
              {text.addFoal}
            </Button>
          </Stack>

          <Grid container spacing={1.5}>
            {foals.length ? foals.map((foal) => (
              <Grid item xs={12} sm={6} md={4} key={foal.foal_id}>
                <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{sexLabel(foal.sex)}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>{foal.birth_time ? String(foal.birth_time).replace('T', ' ').slice(0, 16) : '-'}</Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={statusLabel(foal.status)}
                      sx={{ bgcolor: foal.status === 'alive' ? COLORS.greenSoft : COLORS.roseSoft, fontWeight: 900 }}
                    />
                  </Stack>
                  {foal.markings ? <Typography variant="body2" sx={{ color: COLORS.muted, mt: 0.8 }}>{foal.markings}</Typography> : null}

                  {foal.horse_id ? (
                    <Paper elevation={0} sx={{ mt: 1, p: 1, bgcolor: COLORS.cardSoft, border: `1px solid ${COLORS.line}` }}>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.registeredAs}</Typography>
                      <Typography sx={{ fontWeight: 900, color: COLORS.text }}>
                        {[foal.horse_name_ar, foal.horse_name].filter(Boolean).join(' | ') || `#${foal.horse_id}`}
                      </Typography>
                    </Paper>
                  ) : foal.status === 'alive' ? (
                    <Button fullWidth size="small" startIcon={<CheckIcon />} onClick={() => openRegister(foal)} sx={{ mt: 1, color: COLORS.gold, fontWeight: 900 }}>
                      {text.registerFoal}
                    </Button>
                  ) : (
                    // مولود ميت لا يُسجل خيلاً — وواقعته موثقة هنا على أي حال فلا يضيع شيء.
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 1 }}>{text.notRegisterable}</Typography>
                  )}
                </Paper>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Typography sx={{ color: COLORS.muted, textAlign: 'center', py: 4 }}>{text.noFoals}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* واقعة الولادة: الحقول كلّها اختياريّة عدا وقت خروج المولود — فالولادة تُسجَّل
          غالبًا بعد وقوعها لا أثناءها، وإلزام علامات ما قبلها يعني إمّا تخمينها أو ترك
          الواقعة بلا تسجيل. والوقت وحده إلزاميّ لأنّه ما يُشتقّ منه تاريخ ميلاد المولود. */}
      <Dialog open={foalingDialog} onClose={() => setFoalingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.recordFoaling}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                label={text.foalDelivered}
                InputLabelProps={{ shrink: true }}
                value={foalingForm.foal_delivered_time}
                onChange={(e) => setFoalingForm((v) => ({ ...v, foal_delivered_time: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={text.foalingResult}
                value={foalingForm.foaling_result}
                onChange={(e) => setFoalingForm((v) => ({ ...v, foaling_result: e.target.value }))}
                fullWidth
              >
                {FOALING_RESULTS.map((key) => (
                  <MenuItem key={key} value={key}>{foalingResultLabel(key, text)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                label={text.waterBroken}
                InputLabelProps={{ shrink: true }}
                value={foalingForm.water_broken_time}
                onChange={(e) => setFoalingForm((v) => ({ ...v, water_broken_time: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                label={text.placentaDelivered}
                InputLabelProps={{ shrink: true }}
                value={foalingForm.placenta_delivered_time}
                onChange={(e) => setFoalingForm((v) => ({ ...v, placenta_delivered_time: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label={text.monitoringStart}
                InputLabelProps={{ shrink: true }}
                value={foalingForm.monitoring_start_date}
                onChange={(e) => setFoalingForm((v) => ({ ...v, monitoring_start_date: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>

          <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
            {[
              ['udder_development', text.udderDevelopment],
              ['waxing', text.waxing],
              ['milk_leakage', text.milkLeakage],
              ['pelvic_ligament_relaxation', text.pelvicRelaxation],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={<Checkbox checked={!!foalingForm[key]} onChange={(e) => setFoalingForm((v) => ({ ...v, [key]: e.target.checked }))} />}
                label={label}
              />
            ))}
          </Stack>

          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Checkbox checked={!!foalingForm.emergency_alert} onChange={(e) => setFoalingForm((v) => ({ ...v, emergency_alert: e.target.checked }))} />}
            label={text.emergencyAlert}
          />
          {foalingForm.emergency_alert && (
            <TextField
              label={text.emergencyReason}
              value={foalingForm.emergency_reason}
              onChange={(e) => setFoalingForm((v) => ({ ...v, emergency_reason: e.target.value }))}
              fullWidth
              sx={{ mt: 1 }}
            />
          )}

          <TextField
            label={text.notes}
            value={foalingForm.notes}
            onChange={(e) => setFoalingForm((v) => ({ ...v, notes: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFoalingDialog(false)}>{text.cancel}</Button>
          <Button variant="contained" onClick={submitFoaling} disabled={busy} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
            {text.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={foalDialog} onClose={() => setFoalDialog(false)} fullWidth maxWidth="xs" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.addFoal}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.6} sx={{ mt: 0.5 }}>
            <TextField select fullWidth label={text.sex} value={foalForm.sex} onChange={(e) => setFoalForm((f) => ({ ...f, sex: e.target.value }))}>
              <MenuItem value="">{text.sexUnknown}</MenuItem>
              <MenuItem value="S">{text.male}</MenuItem>
              <MenuItem value="M">{text.female}</MenuItem>
            </TextField>
            <TextField select fullWidth label={text.status} value={foalForm.status} onChange={(e) => setFoalForm((f) => ({ ...f, status: e.target.value }))}>
              <MenuItem value="alive">{text.alive}</MenuItem>
              <MenuItem value="stillborn">{text.stillborn}</MenuItem>
              <MenuItem value="died_after">{text.diedAfter}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="datetime-local"
              label={text.birthTime}
              InputLabelProps={{ shrink: true }}
              value={foalForm.birth_time}
              onChange={(e) => setFoalForm((f) => ({ ...f, birth_time: e.target.value }))}
            />
            <TextField fullWidth label={text.markings} value={foalForm.markings} onChange={(e) => setFoalForm((f) => ({ ...f, markings: e.target.value }))} />
            <TextField fullWidth multiline minRows={3} label={text.notes} value={foalForm.notes} onChange={(e) => setFoalForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFoalDialog(false)}>{text.cancel}</Button>
          <Button variant="contained" disabled={busy} onClick={addFoal} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(registerTarget)} onClose={() => setRegisterTarget(null)} fullWidth maxWidth="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.registerTitle}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1.5 }}>{text.registerNote}</Alert>
          {pedigree && (
            <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.dam}</Typography>
                  <Typography sx={{ fontWeight: 900, color: COLORS.text }}>
                    {pedigree.dam_case_id ? caseName(cases.find((c) => c.case_id === pedigree.dam_case_id)) : text.unknown}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.sire}</Typography>
                  <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{pedigree.sire_stallion_name || text.unknown}</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1.2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.nameAr} value={registerForm.name_arabic} onChange={(e) => setRegisterForm((f) => ({ ...f, name_arabic: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.nameEn} value={registerForm.name} onChange={(e) => setRegisterForm((f) => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={text.owner} value={registerForm.owner_id} onChange={(e) => setRegisterForm((f) => ({ ...f, owner_id: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterTarget(null)}>{text.cancel}</Button>
          <Button variant="contained" disabled={busy} onClick={registerFoal} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.confirmRegister}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', minHeight: 104 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
      </Box>
    </Paper>
  );
}
